import { Student } from '../types/student';
import { GoogleService } from './googleService';
import { supabase } from '../lib/supabase';

interface ExportableStudent {
  nome: string;
  email: string;
  documento: string;
  telefone: string;
  ultimo_acesso: string;
  openings_points: number;
  clicks_points: number;
  interaction_points: number;
  [key: string]: string | number; // Para campos dinâmicos de cursos
}

interface ExportOptions {
  columns: {
    personalInfo: boolean;
    courses: boolean;
    interactions: boolean;
    metrics: boolean;
    dates: boolean;
  };
}

export const exportService = {
  formatStudentData(students: Student[], options: ExportOptions): ExportableStudent[] {
    // Encontra o número máximo de cursos entre todos os alunos
    const maxCursos = Math.max(...students.map(s => s.cursos?.length || 0));

    return students.map(student => {
      // Objeto para armazenar os dados formatados
      const formattedData: ExportableStudent = {} as ExportableStudent;

      // Adiciona informações pessoais se selecionado
      if (options.columns.personalInfo) {
        formattedData.nome = student.nome;
        formattedData.email = student.email;
        formattedData.documento = student.documento;
        formattedData.telefone = student.telefone;
      }

      // Adiciona métricas se selecionado
      if (options.columns.metrics) {
        formattedData.openings_points = student.openings_points || 0;
        formattedData.clicks_points = student.clicks_points || 0;
        formattedData.interaction_points = student.interaction_points || 0;
      }

      // Adiciona datas se selecionado
      if (options.columns.dates) {
        // Verifica se é a data que indica "nunca acessou"
        if (!student.ultimo_acesso || student.ultimo_acesso.includes('0001-01-01 00:00:00 BC')) {
          formattedData.ultimo_acesso = 'Nunca acessou';
        } else {
          formattedData.ultimo_acesso = student.ultimo_acesso;
        }
      }

      // Adiciona cursos se selecionado
      if (options.columns.courses) {
        for (let i = 0; i < maxCursos; i++) {
          const curso = student.cursos?.[i];
          if (curso) {
            formattedData[`Curso ${i + 1}`] = curso.nome_curso;
            formattedData[`Status ${i + 1}`] = curso.status;
            formattedData[`Progresso ${i + 1}`] = curso.progresso;
            if (options.columns.dates) {
              formattedData[`Data Início ${i + 1}`] = curso.data_inicio || 'Não iniciado';
              formattedData[`Previsão Conclusão ${i + 1}`] = curso.data_conclusao_prevista || 'Não definida';
            }
          } else {
            formattedData[`Curso ${i + 1}`] = '';
            formattedData[`Status ${i + 1}`] = '';
            formattedData[`Progresso ${i + 1}`] = '';
            if (options.columns.dates) {
              formattedData[`Data Início ${i + 1}`] = '';
              formattedData[`Previsão Conclusão ${i + 1}`] = '';
            }
          }
        }
      }

      return formattedData;
    });
  },

  async getAllFilteredData(searchTerm: string, filters: any) {
    try {
      // Construir a query base
      let query = supabase
        .from('acm_base')
        .select('*');

      // Aplicar filtros de busca
      if (searchTerm) {
        query = query.or(`nome.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,documento.ilike.%${searchTerm}%`);
      }

      // Aplicar filtros específicos
      if (filters.courseStatus && filters.courseStatus !== 'all') {
        query = query.eq('status_matricula', filters.courseStatus);
      }

      // Aplicar filtro de curso
      if (filters.nome_curso && filters.nome_curso !== 'todos') {
        query = query.eq('nome_curso', filters.nome_curso);
      }

      // Filtro por período de acesso
      if (filters.dateRange && filters.dateRange !== 'all') {
        const days = parseInt(filters.dateRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - days);
        query = query.gte('ultimo_acesso', cutoffDate.toISOString());
      }

      // Filtro por nível de interação
      if (filters.interactionLevel && filters.interactionLevel !== 'all') {
        switch (filters.interactionLevel) {
          case 'high':
            query = query.gte('interaction_points', 100);
            break;
          case 'medium':
            query = query.and(`interaction_points.gte.50,interaction_points.lt.100`);
            break;
          case 'low':
            query = query.and(`interaction_points.gt.0,interaction_points.lt.50`);
            break;
          case 'none':
            query = query.eq('interaction_points', 0);
            break;
        }
      }

      // Filtro para incluir/excluir inativos
      if (!filters.includeInactive) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        query = query.gte('ultimo_acesso', thirtyDaysAgo.toISOString());
      }

      // Buscar todos os dados
      const { data: records, error } = await query;
      
      if (error) throw error;
      if (!records) return [];

      // Agrupar dados por aluno
      const studentMap = new Map<string, Student>();
      
      records.forEach(record => {
        if (!record.email || !record.nome) return;

        if (!studentMap.has(record.email)) {
          studentMap.set(record.email, {
            email: record.email,
            nome: record.nome,
            documento: record.documento || '',
            telefone: record.telefone || '',
            ultimo_acesso: record.ultimo_acesso,
            cursos: [],
            openings_points: record.openings_points || 0,
            clicks_points: record.clicks_points || 0,
            interaction_points: record.interaction_points || 0
          });
        }

        const student = studentMap.get(record.email)!;
        
        if (record.nome_curso) {
          student.cursos.push({
            nome_curso: record.nome_curso,
            status: record.status_matricula || '',
            progresso: record.progresso || 0,
            data_inicio: record.data_inicio,
            data_conclusao_prevista: record.data_conclusao_prevista
          });
        }
      });

      return Array.from(studentMap.values());
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      throw new Error('Erro ao buscar dados para exportação');
    }
  },

  async exportToGoogleSheets(students: Student[], options: ExportOptions) {
    try {
      const formattedData = this.formatStudentData(students, options);
      const headers = Object.keys(formattedData[0]);
      const rows = formattedData.map(student => Object.values(student));
      const allData = [headers, ...rows];

      const googleService = GoogleService.getInstance();
      const title = `Lista de Alunos - ${new Date().toLocaleDateString('pt-BR')}`;
      
      // Cria a planilha e retorna a URL
      const spreadsheetUrl = await googleService.createSpreadsheet(title, allData);
      
      // Abre a planilha em uma nova aba
      window.open(spreadsheetUrl, '_blank');
    } catch (error) {
      console.error('Erro ao exportar para Google Sheets:', error);
      throw new Error('Não foi possível exportar para o Google Sheets');
    }
  },

  async exportToCSV(students: Student[], options: ExportOptions) {
    try {
      const formattedData = this.formatStudentData(students, options);
      const headers = Object.keys(formattedData[0]);
      const rows = formattedData.map(student => Object.values(student));
      
      // Converte para CSV
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Cria o blob e faz o download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `alunos_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Erro ao exportar para CSV:', error);
      throw new Error('Não foi possível exportar para CSV');
    }
  },

  async exportToExcel(students: Student[], options: ExportOptions) {
    try {
      const formattedData = this.formatStudentData(students, options);
      const headers = Object.keys(formattedData[0]);
      const rows = formattedData.map(student => Object.values(student));
      
      // Cria o conteúdo Excel (formato básico XML)
      const excelContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
          <head>
            <meta charset="UTF-8">
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>Alunos</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml>
            <![endif]-->
          </head>
          <body>
            <table>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
              ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </table>
          </body>
        </html>
      `;

      // Cria o blob e faz o download
      const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `alunos_${new Date().toISOString().split('T')[0]}.xls`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      throw new Error('Não foi possível exportar para Excel');
    }
  }
};